import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import queryString from 'query-string'
import callAPI from '../../../helpers/callAPI'
import Loading from '../../../templates/UI/Loading/Loading'
import Dollars from '../../../templates/Dollars/Dollars'
import { SystemMessageContext } from '../../../context/SystemMessageContext'
import { useLocation } from 'react-router-dom'
import numberSort from '../../../helpers/numberSort'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import Campaign from '../../../templates/Campaign/Campaign'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import DownloadCSV from '../../../templates/DownloadCSV/DownloadCSV'

export default function AdministratorMarginDetailsReport({id, year, quarter, propMode}) {

    const location = useLocation()
    let urlParams = queryString.parse(location.search)

    const resolvedYear = year || (urlParams.year ? Number(urlParams.year) : null)
    const resolvedQuarter = quarter || (urlParams.quarter ? Number(urlParams.quarter) : null)
    const isQuarterMode = !!(id && resolvedYear && resolvedQuarter)

    const { showSystemMessage } = useContext(SystemMessageContext)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [report, setReport] = useState([])
    const [totals, setTotals] = useState([])
    const [admin, setAdmin] = useState({})
    const [date, setDate] = useState({month: moment().month, year: moment().year(), quarter: null})
    const [mode, setMode] = useState(propMode ? propMode : urlParams.mode == 'existing' ? 'existing' : urlParams.mode == 'new' ? 'new' : 'all')

    useEffect(() => {
        getReport()
    }, [id, mode, resolvedYear, resolvedQuarter])

    const getReport = async () => {
        setDataLoaded(false)

        const url = isQuarterMode
            ? `reports/administratorMarginDetailsReport/${id}?${queryString.stringify({
                year: resolvedYear,
                quarter: resolvedQuarter,
                mode,
            })}`
            : `reports/administratorMarginDetailsReport/${id}?mode=${mode}`

        callAPI(url, 'GET')
            .then((result) => {
                if (result['success'] === true) {
                    result.admin && setAdmin(result.admin)
                    setReport(result.report)
                    setTotals(result.totals)
                    setDate({month: result.month, year: result.year, quarter: result.quarter || null})
                    setDataLoaded(true)
                } else if (result['success'] === false) {
                    showSystemMessage('error', result.errors.message)
                    setDataLoaded(true)
                } else {
                    showSystemMessage('error', 'Could not load report.')
                    setDataLoaded(true)
                }
            })
            .catch((error) => {
                console.log(`Error while fetching Administrator Margin Details report: ${error.message}`)
                showSystemMessage('error', `An error occurred while fetching Administrator Margin Details report: ${error.message}`)
                setDataLoaded(true)
            })
    }

    const periodLabel = date.quarter
        ? `Q${date.quarter} ${date.year}`
        : moment().month(date.month - 1).year(date.year).format('MMMM YYYY')

    const getReportOutput = () => {

        let columns = [
            {
                Header: 'Affiliate Id',
                sortType: numberSort,
                accessor: 'affiliate_id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Affiliate',
                accessor: 'affiliate_name',
                Cell: (tableData) => {
                    return <Affiliate aid={tableData.data[tableData.row.index].affiliate_id} name={tableData.data[tableData.row.index].name} priority={tableData.data[tableData.row.index].aff_priority_value} status={tableData.data[tableData.row.index].aff_status_value} />
                },
            },
            {
                Header: 'Campaign Id',
                sortType: numberSort,
                accessor: 'offer_id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Campaign',
                accessor: 'campaign_name',
                Cell: (tableData) => {
                    return <Campaign oid={tableData.data[tableData.row.index].offer_id} name={tableData.data[tableData.row.index].offer_name} status={tableData.data[tableData.row.index].status_value} affiliateStatus={tableData.data[tableData.row.index].affiliate_offer_status}/>
                },
            },
            {
                Header: 'Margin Type',
                accessor: 'margin_type',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Margin</div>,
                sortType: numberSort,
                accessor: 'margin',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: 'Notes',
                accessor: 'notes',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
        ]

        return (
            <React.Fragment>
                <ReportTable data={report} columns={columns} totals={totals} />
            </React.Fragment>
        )
    }

    return (

        <div className="card pb-0">
            <div className='d-flex align-items-center justify-content-between p-3 flex-wrap'>
                <div className="mr-4 d-flex align-items-center">
                    <div className="ml-2 mb-h1-dark">Administrator Margin Details Report</div>
                    <div className="form-group m-0 ml-3">
                        <div className="form-control">
                            <input type="radio" id="mode-all" name="mode" value="all" checked={mode === 'all'} onChange={(e) => setMode(e.target.value)} /> <label htmlFor="mode-all">All</label>
                            <input type="radio" id="mode-existing" name="mode" value="existing" checked={mode === 'existing'} onChange={(e) => setMode(e.target.value)} className='ml-2' /> <label htmlFor="mode-existing">Existing</label>
                            <input type="radio" id="mode-new" name="mode" value="new" checked={mode === 'new'} onChange={(e) => setMode(e.target.value)} className='ml-2' /> <label htmlFor="mode-new">New</label>
                        </div>
                    </div>
                </div>
                {dataLoaded && report.length > 0 && (
                    <DownloadCSV
                        filename={`Administrator Margin Details - ${admin.name} - ${periodLabel}`}
                        headerLabels={['Affiliate Id', 'Affiliate', 'Campaign Id', 'Campaign', 'Margin Type', 'Margin', 'Notes']}
                        data={report}
                        dataLabels={['affiliate_id', 'name', 'offer_id', 'offer_name', 'margin_type', 'margin', 'notes']}
                    />
                )}
            </div>
            {
                dataLoaded &&
                <div className='d-flex align-items-center px-3 pb-3 flex-wrap'>
                    <div className="alert alert-primary mb-0">{admin.name} - {periodLabel}</div>
                </div>
            }
            <div>
                {dataLoaded ? (
                    report.length === 0 ? (
                        <div className="p-3">No stats for the current parameters.</div>
                    ) : (
                        <React.Fragment>
                            {getReportOutput()}
                        </React.Fragment>
                    )
                ) : (
                    <Loading></Loading>
                )}
            </div>
        </div>
    )
}